import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Truck, Edit, Trash2, FilterX } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppButton, ConfirmModal, AppModal, FormField, Input, Textarea,
  FilterToolbar, AppDataTable, ActionMenu, ActionMenuItem,
  ActionMenuDivider, applyColumnFilters, sortRows, isColumnFilterActive,
} from "../ui";

/**
 * AssetVendorsPage — suppliers and AMC providers.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: the endpoint returns every vendor in one response, so
 * filtering, sorting and paging all happen in the browser over the whole list
 * rather than over one page of it.
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

/** The vendor form, field by field. Kept as data so the modal is a loop rather
 *  than seven near-identical blocks that drift apart. */
const FORM_FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "contactPerson", label: "Contact Person" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "email", label: "Email", type: "email" },
  { key: "gstin", label: "GSTIN" },
  { key: "website", label: "Website", type: "url" },
  { key: "address", label: "Address" },
];

const blank = {
  name: "", contactPerson: "", phone: "", email: "", gstin: "",
  address: "", website: "", notes: "", isActive: true,
};

const AssetVendorsPage = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/assets/vendors");
      setItems(data.vendors || []);
    } catch {
      showToast("Failed to load vendors.", "error");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(blank); setShowModal(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...blank, ...v }); setShowModal(true); };

  const columns = useMemo(() => [
    {
      key: "name",
      label: "Name",
      sortable: true,
      filter: { type: "text", placeholder: "Name or contact…" },
      // Both are visible in the row, so one text filter has to reach both.
      filterValue: (v) => `${v.name || ""} ${v.contactPerson || ""}`,
      sortValue: (v) => v.name || "",
      render: (v) => <span className="tw-font-medium tw-text-content">{v.name}</span>,
    },
    {
      key: "contactPerson",
      label: "Contact",
      sortable: true,
      filter: "text",
      filterValue: (v) => v.contactPerson || "",
      render: (v) => v.contactPerson || DASH,
    },
    {
      key: "phone",
      label: "Phone",
      sortable: true,
      filter: "text",
      filterValue: (v) => v.phone || "",
      render: (v) => (v.phone
        ? <a href={`tel:${v.phone}`} onClick={(e) => e.stopPropagation()} className="tw-text-primary hover:tw-underline">{v.phone}</a>
        : DASH),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      filter: "text",
      filterValue: (v) => v.email || "",
      render: (v) => (v.email
        ? <a href={`mailto:${v.email}`} onClick={(e) => e.stopPropagation()} className="tw-text-primary hover:tw-underline">{v.email}</a>
        : DASH),
    },
    {
      key: "gstin",
      label: "GSTIN",
      sortable: true,
      filter: "text",
      filterValue: (v) => v.gstin || "",
      render: (v) => (v.gstin
        ? <span className="tw-font-mono tw-text-[0.75rem]">{v.gstin}</span>
        : DASH),
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also opens the editor.
      render: (v) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${v.name}`}>
            <ActionMenuItem onClick={() => openEdit(v)}>
              <Edit size={14} /> Edit
            </ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setDeleteTarget(v)}>
              <Trash2 size={14} /> Remove
            </ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ], []);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  const visible = useMemo(
    () => sortRows(applyColumnFilters(items, columns, columnFilters), columns, sort),
    [items, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast("Name is required.", "error");
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await apiClient.put(`/assets/vendors/${editing._id}`, form);
        showToast("Vendor updated.");
      } else {
        await apiClient.post("/assets/vendors", form);
        showToast("Vendor created.");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/assets/vendors/${deleteTarget._id}`);
      showToast("Vendor removed.");
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Delete failed.", "error");
      setDeleteTarget(null);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        backButton
        onBack={() => navigate("/assets")}
        icon={Truck}
        title="Asset Vendors"
        description="Suppliers and AMC providers for your assets"
      />

      <AppDataTable
        fill={false}
        resizable
        storageKey="asset-vendors"
        toolbar={
          <FilterToolbar
            flush
            actions={
              <AppButton size="sm" onClick={openCreate}><Plus size={16} /> New Vendor</AppButton>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === items.length
                ? `${items.length} vendor${items.length === 1 ? "" : "s"}`
                : `${visible.length} of ${items.length}`}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(v) => v._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — a key
            // sitting there holding nothing would keep "Clear filters" on
            // screen after the last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={openEdit}
        emptyMessage={filtersOn
          ? "No vendor matches these filters."
          : "No vendors yet. Add suppliers to track purchases."}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "vendors",
        } : undefined}
      />

      <AppModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={editing ? "Edit Vendor" : "New Vendor"}
        size="md"
        /* ⚠️ The modal's own footer slot, NOT `FormActionBar` — that one is
           `position: fixed` for a full-page form and would float over the
           viewport rather than sit under these fields. */
        footer={
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <AppButton variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </AppButton>
            {/* A busy state is `disabled` plus a changed label — AppButton takes
                no `loading` prop. */}
            <AppButton onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </AppButton>
          </div>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          {FORM_FIELDS.map(({ key, label, required, type }) => (
            <FormField key={key} label={label} required={required}>
              <Input
                type={type}
                value={form[key] || ""}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </FormField>
          ))}
          <FormField label="Notes">
            <Textarea
              rows={2}
              value={form.notes || ""}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </FormField>
        </div>
      </AppModal>

      <ConfirmModal
        show={!!deleteTarget}
        title="Remove Vendor?"
        message={deleteTarget ? `Delete "${deleteTarget.name}"?` : ""}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onHide={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AssetVendorsPage;
